import { useState } from "react";
import Icon from "../../components/Icon";

type ChipEditorProps = {
  items: string[];
  onAdd: (value: string) => Promise<void>;
  onRemove: (value: string) => Promise<void>;
  label: string;
  placeholder: string;
};

const CHIP: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "3px 8px 3px 10px",
  background: "var(--surface-sunk)",
  border: "1px solid var(--rule)",
  borderRadius: "var(--r-md)",
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  color: "var(--fg-muted)",
};

const EYEBROW: React.CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.1em",
  color: "var(--fg-muted)",
  fontWeight: 500,
};

export default function ChipEditor({
  items: initialItems,
  onAdd,
  onRemove,
  label,
  placeholder,
}: ChipEditorProps) {
  const [items, setItems] = useState<string[]>(initialItems);
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  async function handleAdd() {
    const val = draft.trim();
    if (!val || items.includes(val)) return;
    setAdding(true);
    setError(null);
    try {
      await onAdd(val);
      setItems((prev) => [...prev, val]);
      setDraft("");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(val: string) {
    setRemoving((prev) => new Set(prev).add(val));
    setError(null);
    try {
      await onRemove(val);
      setItems((prev) => prev.filter((x) => x !== val));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRemoving((prev) => {
        const s = new Set(prev);
        s.delete(val);
        return s;
      });
    }
  }

  return (
    <div className="field-group" style={{ marginBottom: 16 }}>
      <div className="field-group-head">
        <span style={EYEBROW}>{label}</span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-muted)" }}>
          {items.length}
        </span>
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: items.length ? 10 : 0 }}>
        {items.map((item) => (
          <span key={item} style={CHIP}>
            {item}
            <button
              onClick={() => void handleRemove(item)}
              disabled={removing.has(item)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "1px 0 0",
                color: "var(--fg-subtle)",
                display: "flex",
                opacity: removing.has(item) ? 0.4 : 1,
              }}
              title={`Remove ${item}`}
            >
              <Icon name="x" size={11} stroke={2} />
            </button>
          </span>
        ))}
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        <input
          className="input"
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void handleAdd()}
          style={{ flex: 1 }}
        />
        <button
          className="btn btn-quiet btn-sm"
          onClick={() => void handleAdd()}
          disabled={adding || !draft.trim()}
        >
          {adding ? "Adding…" : "Add"}
        </button>
      </div>

      {error && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            fontFamily: "var(--font-mono)",
            color: "#e58a6d",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
